import { Track } from 'livekit-client';

export type ShortcutAction =
  | 'microphone'
  | 'camera'
  | 'raiseHand'
  | 'chat'
  | 'screenShare'
  | 'leave';

type ShortcutMatch = {
  action: ShortcutAction;
  code: string;
  alt?: boolean;
  shift?: boolean;
};

export type ShortcutPermissions = {
  canPublish?: boolean;
  canPublishData?: boolean;
  canPublishSources?: number[];
  canUpdateOwnMetadata?: boolean;
};

export const SCREEN_SHARE_CAPTURE_OPTIONS = {
  audio: true,
  selfBrowserSurface: 'include',
} as const;

export function trackSourceToProtocol(source: Track.Source): number {
  switch (source) {
    case Track.Source.Camera:
      return 1;
    case Track.Source.Microphone:
      return 2;
    case Track.Source.ScreenShare:
      return 3;
    default:
      return 0;
  }
}

const sourceToProtocol = {
  microphone: trackSourceToProtocol(Track.Source.Microphone),
  camera: trackSourceToProtocol(Track.Source.Camera),
  screenShare: trackSourceToProtocol(Track.Source.ScreenShare),
} as const;

export const SHORTCUTS: ShortcutMatch[] = [
  { action: 'microphone', code: 'KeyD' },
  { action: 'camera', code: 'KeyE' },
  { action: 'raiseHand', code: 'KeyY', shift: true },
  { action: 'chat', code: 'KeyC', alt: true },
  { action: 'screenShare', code: 'KeyS', shift: true },
  { action: 'leave', code: 'KeyH', shift: true },
];

export function isShortcutAllowed(
  action: ShortcutAction,
  permissions: ShortcutPermissions | undefined,
): boolean {
  if (action === 'leave') return true;
  if (!permissions) return false;
  if (action === 'chat') return permissions.canPublishData === true;
  if (action === 'raiseHand') return permissions.canUpdateOwnMetadata === true;

  return (
    permissions.canPublish === true &&
    (permissions.canPublishSources?.length === 0 ||
      permissions.canPublishSources?.includes(sourceToProtocol[action]) === true)
  );
}

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || typeof target !== 'object') return false;
  const element = target as HTMLElement;
  const tagName = element.tagName?.toLowerCase();
  return (
    element.isContentEditable ||
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select'
  );
}

export function matchShortcut(event: KeyboardEvent): ShortcutAction | null {
  if (event.repeat || isEditableTarget(event.target) || (!event.ctrlKey && !event.metaKey)) {
    return null;
  }

  const shortcut = SHORTCUTS.find(
    (candidate) =>
      event.code === candidate.code &&
      event.altKey === !!candidate.alt &&
      event.shiftKey === !!candidate.shift,
  );
  return shortcut?.action ?? null;
}
